import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuArrowLeft, LuChevronLeft, LuChevronRight, LuDownload } from "react-icons/lu";
import toast from "react-hot-toast";
import SidebarLayout from "../../PageLayout/SidebarLayout";
import {
  downloadSyncJobsCsv,
  fetchConnectors,
  fetchSyncJobs,
  getApiErrorMessage,
} from "../connectors/integrationsService";
import { formatJobTime } from "../connectors/helpers";

const PAGE_SIZE = 10;

function statusClass(status) {
  if (status === "Failed") return "bg-[#FCEBEB] text-[#CD0F0F]";
  if (status === "Running") return "bg-[#FFF4E5] text-[#EF9F27]";
  return "bg-[#EAF3DE] text-[#528416]";
}

export default function SyncHistory() {
  const navigate = useNavigate();
  const [connectors, setConnectors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [connectorId, setConnectorId] = useState("");
  const [status, setStatus] = useState("");
  const [range, setRange] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchConnectors({ page: 1, page_size: 100 })
      .then((res) => setConnectors(res.items || []))
      .catch(() => setConnectors([]));
  }, []);

  const loadJobs = useCallback(async () => {
    setError("");
    const res = await fetchSyncJobs({
      connector_id: connectorId || undefined,
      status: status || undefined,
      range: range || undefined,
      page: 1,
      page_size: 100,
    });
    setJobs(res.items);
    setTotal(Number(res.total ?? res.items.length));
  }, [connectorId, status, range]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadJobs();
      } catch (err) {
        if (!cancelled) {
          setJobs([]);
          setTotal(0);
          setError(getApiErrorMessage(err, "Could not load sync history."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadJobs]);

  const pageCount = Math.max(1, Math.ceil((jobs.length || total) / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const slice = jobs.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const from = jobs.length === 0 ? 0 : (current - 1) * PAGE_SIZE + 1;
  const to = Math.min(current * PAGE_SIZE, jobs.length);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadSyncJobsCsv({
        connector_id: connectorId || undefined,
        status: status || undefined,
        range: range || undefined,
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not export sync history."));
    } finally {
      setExporting(false);
    }
  };

  const connectorOptions = useMemo(
    () => connectors.filter((c) => c.id && c.name),
    [connectors]
  );

  const exportButton = (
    <button
      type="button"
      onClick={handleExport}
      disabled={exporting}
      className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-500 text-white text-sm hover:bg-white/10 disabled:opacity-50"
    >
      <LuDownload className="h-4 w-4" />
      {exporting ? "Exporting…" : "Export CSV"}
    </button>
  );

  return (
    <SidebarLayout
      pageTitle="Sync History"
      breadcrumb="Dashboard / Settings / Sync History"
      headerActions={<div className="hidden md:block">{exportButton}</div>}
    >
      <div className="p-4 md:p-6 max-w-[1400px] mx-auto w-full min-w-0">
        <button
          type="button"
          onClick={() => navigate("/settings?tab=integrations")}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F172A] mb-5"
        >
          <LuArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select
            value={connectorId}
            onChange={(e) => { setConnectorId(e.target.value); setPage(1); }}
            className="h-10 min-w-[140px] rounded-lg border border-[#E5E7EB] bg-white px-3 text-sm text-[#031F36] focus:outline-none focus:ring-2 focus:ring-[#37B5A0]/30"
          >
            <option value="">Connector</option>
            {connectorOptions.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="h-10 min-w-[120px] rounded-lg border border-[#E5E7EB] bg-white px-3 text-sm text-[#031F36] focus:outline-none focus:ring-2 focus:ring-[#37B5A0]/30"
          >
            <option value="">Status</option>
            <option value="Success">Success</option>
            <option value="Failed">Failed</option>
          </select>
          <select
            value={range}
            onChange={(e) => { setRange(e.target.value); setPage(1); }}
            className="h-10 min-w-[140px] rounded-lg border border-[#E5E7EB] bg-white px-3 text-sm text-[#031F36] focus:outline-none focus:ring-2 focus:ring-[#37B5A0]/30"
          >
            <option value="">Date range</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="md:hidden inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#031F36] disabled:opacity-50"
          >
            <LuDownload className="h-4 w-4" />
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>

        <div className="bg-white border border-[#E6E7EB] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] text-sm">
              <thead>
                <tr className="bg-[#FAFBFD] text-left text-[12px] leading-[14px] font-medium text-[#898F99]">
                  <th className="px-4 py-3">Connector</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Finished</th>
                  <th className="px-4 py-3">Duration</th>
                  <th className="px-4 py-3">Documents Processed</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Error Reason</th>
                  <th className="px-4 py-3">Triggered By</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-sm text-[#898F99]">
                      Loading sync jobs…
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-sm text-red-600">
                      {error}
                    </td>
                  </tr>
                ) : slice.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-sm text-[#898F99]">
                      {!connectorId && !status && !range
                        ? "No sync jobs"
                        : "No sync jobs match your filters."}
                    </td>
                  </tr>
                ) : (
                  slice.map((job) => {
                    const failed = job.status === "Failed";
                    return (
                      <tr
                        key={job.id}
                        className="h-[76px] border-b border-[#E6E7EB] last:border-b-0 hover:bg-[#F6F7FA]"
                      >
                        <td className="px-4 py-3 align-middle text-[12px] leading-[14px] font-medium text-[#031F36]">
                          {job.connector}
                        </td>
                        <td className="px-4 py-3 align-middle text-[12px] leading-[14px] text-[#031F36] whitespace-nowrap">
                          {formatJobTime(job.started)}
                        </td>
                        <td className="px-4 py-3 align-middle text-[12px] leading-[14px] text-[#031F36] whitespace-nowrap">
                          {formatJobTime(job.finished)}
                        </td>
                        <td className="px-4 py-3 align-middle text-[12px] leading-[14px] text-[#031F36]">
                          {job.duration}
                        </td>
                        <td className="px-4 py-3 align-middle text-[12px] leading-[14px] text-[#031F36]">
                          {Number(job.documents).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <span
                            className={`inline-flex items-center justify-center rounded-[20px] px-[9px] py-1 text-[12px] leading-[14px] font-medium ${statusClass(job.status)}`}
                          >
                            {job.status}
                          </span>
                        </td>
                        <td
                          className={`px-4 py-3 align-middle text-[12px] leading-[14px] ${
                            failed ? "text-[#CD0F0F]" : "text-[#898F99]"
                          }`}
                        >
                          {job.error || "—"}
                        </td>
                        <td className="px-4 py-3 align-middle text-[12px] leading-[14px] text-[#031F36]">
                          {job.triggeredBy || "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E6E7EB]">
            <p className="text-[12px] leading-[14px] text-[#898F99]">
              Showing {from}–{to} of {jobs.length} sync jobs
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={current <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#031F36] disabled:opacity-40 hover:bg-[#F6F7FA]"
                aria-label="Previous page"
              >
                <LuChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  className={`h-8 min-w-8 px-2 text-[12px] leading-[14px] font-medium ${
                    n === current
                      ? "rounded-full text-white"
                      : "rounded-md border border-[#E6E7EB] bg-white text-[#031F36] hover:bg-[#F6F7FA]"
                  }`}
                  style={n === current ? { backgroundColor: "#37B5A0" } : undefined}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                disabled={current >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#031F36] disabled:opacity-40 hover:bg-[#F6F7FA]"
                aria-label="Next page"
              >
                <LuChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
}
